<template>
  <div
    v-if="currentCourse.length > 0"
    class="print-pdf unit container edit-user-unit-container"
    :class="currentCourse[0].baseYear"
  >
    <div class="pdf-background-img-container">
      <div class="pdf-background-image"></div>
    </div>
    <div class="contentSection section-pdf-container pt-8">
      <div class="hideonprint">
        <v-alert border="start" color="rgb(165, 216, 103)"
          ><h4>{{ $t('coursePdf.instructionsTitle') }}</h4>
          <p>
            1.
            {{
              $t('coursePdf.instructionsStep1Button', {
                button: $t('courseDrawer.currentCourse.downloadSection'),
              })
            }}
          </p>
          <p>2. {{ $t('coursePdf.instructionsStep2') }}</p>
          <p>3. {{ $t('coursePdf.instructionsStep3') }}</p>
          <h4>{{ $t('coursePdf.highlightsTitle') }}</h4>
          <p>{{ $t('coursePdf.highlightsStep') }}</p>
        </v-alert>
        <v-btn
          variant="outlined"
          target="_blank"
          class="unit-icon mt-4 mb-10"
          :class="currentCourse[0].baseYear"
          @click="printPDF()"
        >
          {{ $t('courseDrawer.currentCourse.downloadSection') }}
        </v-btn>
      </div>
      <h1 id="section-title">{{ sectionTitle }}</h1>
      <div v-if="unit">
        <div v-for="(u, index) in unit" :key="u.id">
          <div
            class="unit-meta-container"
            :class="{ 'break-page-before': index !== 0 }"
          >
            <div class="mb-0 mt-4">
              <h2
                id="unit-title"
                class="base-year"
                :class="currentCourse[0].baseYear"
                v-html="u.title"
              ></h2>
            </div>
            <div class="mb-7 mt-0">
              <span class="font-weight-bold" v-html="u.subtitle"></span>
            </div>
          </div>

          <v-flex xs12>
            <span v-if="u.listen || u.complete || u.continueLearning">
              <v-divider class="dashed my-10"></v-divider>

              <div id="start" class="font-weight-bold px-10 mb-3">
                {{ $t('unitStudy.sections.assignments') }}
              </div>
              <div class="unit-assignments px-10 mb-10">
                <div v-if="u.listen">
                  <div class="flex-this mb-8">
                    <div v-if="!onLine" class="flex-this align-baseline">
                      <input
                        type="checkbox"
                        id="listen-checkbox"
                        disabled
                        v-model="listenConfirmed"
                        class="mt-0 pt-0 mr-3"
                      />
                      <div class="flex-this" style="margin-top: 2px">
                        <span class="font-weight-bold">{{ listenLabel }}:</span>
                      </div>
                    </div>
                    <div v-else>
                      <v-checkbox
                        v-model="listenConfirmed"
                        hide-details
                        disabled
                        class="mt-0 pt-0"
                      >
                        <template #label>
                          <div class="flex-this" style="margin-top: 2px">
                            <span class="font-weight-bold"
                              >{{ listenLabel }}:</span
                            >
                          </div>
                        </template>
                      </v-checkbox>
                    </div>
                    <span v-html="u.listen" class="label-text"></span>
                  </div>
                </div>
                <div v-if="u.complete">
                  <div class="flex-this mb-3">
                    <div v-if="!onLine" class="flex-this align-baseline">
                      <input
                        type="checkbox"
                        id="complete-checkbox"
                        disabled
                        v-model="completeConfirmed"
                        class="mt-0 pt-0 mr-3"
                      />
                      <div class="flex-this" style="margin-top: 2px">
                        <span class="font-weight-bold"
                          >{{ completeLabel }}:</span
                        >
                      </div>
                    </div>
                    <div v-else>
                      <v-checkbox
                        v-model="completeConfirmed"
                        hide-details
                        disabled
                        class="mt-0 pt-0"
                      >
                        <template #label>
                          <div class="flex-this" style="margin-top: 2px">
                            <span class="font-weight-bold"
                              >{{ completeLabel }}:</span
                            >
                          </div>
                        </template>
                      </v-checkbox>
                    </div>
                    <span v-html="u.complete" class="label-text"></span>
                  </div>
                </div>
                <div v-if="u.continueLearning">
                  <div class="flex-this">
                    <div v-if="!onLine" class="flex-this align-baseline">
                      <input
                        type="checkbox"
                        id="continue-checkbox"
                        disabled
                        v-model="continueConfirmed"
                        class="mt-0 pt-0 mr-3"
                      />
                      <div class="flex-this" style="margin-top: 2px">
                        <span class="font-weight-bold"
                          >{{ continueLearningLabel }}:</span
                        >
                      </div>
                    </div>
                    <div v-else>
                      <v-checkbox
                        v-model="continueConfirmed"
                        hide-details
                        disabled
                        class="mt-0 pt-0"
                      >
                        <template #label>
                          <div class="flex-this" style="margin-top: 2px">
                            <span class="font-weight-bold"
                              >{{ continueLearningLabel }}:</span
                            >
                          </div>
                        </template>
                      </v-checkbox>
                    </div>
                    <span v-html="u.continueLearning" class="label-text"></span>
                  </div>
                </div>
              </div>
            </span>

            <div id="truth" class="_break-page-before">
              <div class="flex-container mt-5">
                <div
                  class="font-weight-bold text-h6 base-year"
                  :class="currentCourse[0].baseYear"
                >
                  {{ $t('unitStudy.sections.truth') }}
                </div>
                <v-divider class="dashed" style="margin-top: 15px"></v-divider>
              </div>

              <UserVersesQuotes
                :content="u.truthVersesQuotes"
                section="truthVersesQuotes"
              />

              <v-divider v-if="u.truthVersesQuotes" class="dashed"></v-divider>

              <div
                v-if="u.truthInBible"
                v-html="u.truthInBible"
                class="_print-content-container mb-7 mt-12"
              ></div>

              <UserStudyQuestions
                :unitbackdoor="unitbackdoor"
                :backdoor="backdoor"
                :content="u.truthStudyQuestions"
                :unitID="u.id"
                status="archived"
                print="pdf"
                section="truthStudyQuestions"
                @allstudyquestions="studyQuestionsChild"
                @has-loaded="contentHasLoaded"
              />

              <div
                v-if="u.truthBodyContent"
                v-html="u.truthBodyContent"
                class="_print-content-container mb-7 mt-3"
              ></div>
            </div>

            <div id="equip" class="break-page-before">
              <div class="flex-container">
                <div
                  class="font-weight-bold text-h6 base-year"
                  :class="currentCourse[0].baseYear"
                >
                  {{ $t('unitStudy.sections.equipping') }}
                </div>
                <v-divider class="dashed" style="margin-top: 15px"></v-divider>
              </div>

              <div
                v-if="u.equipBodyContent"
                v-html="u.equipBodyContent"
                class="_print-content-container mb-7 mt-3"
              ></div>

              <UserStudyQuestions
                :unitbackdoor="unitbackdoor"
                :backdoor="backdoor"
                :content="u.equipStudyQuestions"
                :unitID="u.id"
                status="archived"
                print="pdf"
                section="equipStudyQuestions"
                @allstudyquestions="studyQuestionsChild"
                @has-loaded="contentHasLoaded"
              />
            </div>

            <div id="application" class="break-page-before">
              <div class="flex-container">
                <div
                  class="font-weight-bold text-h6 base-year"
                  :class="currentCourse[0].baseYear"
                >
                  {{ $t('unitStudy.sections.accountability') }}
                </div>
                <v-divider class="dashed" style="margin-top: 15px"></v-divider>
              </div>

              <div
                v-if="u.accountBodyContent"
                v-html="u.accountBodyContent"
                class="_print-content-container mb-7 mt-3"
              ></div>

              <UserStudyQuestions
                :unitbackdoor="unitbackdoor"
                :backdoor="backdoor"
                :content="u.accountStudyQuestions"
                :unitID="u.id"
                status="archived"
                print="pdf"
                section="accountStudyQuestions"
                @allstudyquestions="studyQuestionsChild"
                @has-loaded="contentHasLoaded"
              />
            </div>

            <div id="mission" class="break-page-before">
              <div class="flex-container">
                <div
                  class="font-weight-bold text-h6 base-year"
                  :class="currentCourse[0].baseYear"
                >
                  {{ $t('unitStudy.sections.mission') }}
                </div>
                <v-divider class="dashed" style="margin-top: 15px"></v-divider>
              </div>

              <div
                v-if="u.missionBodyContent"
                v-html="u.missionBodyContent"
                class="_print-content-container mb-7 mt-3"
              ></div>

              <UserStudyQuestions
                :unitbackdoor="unitbackdoor"
                :backdoor="backdoor"
                :content="u.missionStudyQuestions"
                :unitID="u.id"
                status="archived"
                print="pdf"
                section="missionStudyQuestions"
                @allstudyquestions="studyQuestionsChild"
                @has-loaded="contentHasLoaded"
              />
            </div>

            <div id="supplication" class="break-page-before">
              <div class="flex-container">
                <div
                  class="font-weight-bold text-h6 base-year"
                  :class="currentCourse[0].baseYear"
                >
                  {{ $t('unitStudy.sections.supplication') }}
                </div>
                <v-divider class="dashed" style="margin-top: 15px"></v-divider>
              </div>

              <div v-if="u.supplicationverse" class="my-12 text-center">
                <div v-html="u.supplicationverse"></div>
                <div
                  v-html="u.supplicationreference"
                  class="mt-3 mb-12 font-weight-bold"
                ></div>

                <v-divider class="dashed"></v-divider>
              </div>

              <div
                v-if="u.supplicationBodyContent"
                v-html="u.supplicationBodyContent"
                class="_print-content-container mb-7 mt-3"
              ></div>

              <UserStudyQuestions
                :unitbackdoor="unitbackdoor"
                :backdoor="backdoor"
                :content="u.supplicationStudyQuestions"
                :unitID="u.id"
                status="archived"
                print="pdf"
                section="supplicationStudyQuestions"
                @allstudyquestions="studyQuestionsChild"
                @has-loaded="contentHasLoaded"
              />
            </div>

            <div
              v-if="u.unitNotes"
              id="usernotes"
              class="break-page-before break-page-after"
            >
              <div class="flex-container">
                <div
                  class="font-weight-bold text-h6 base-year"
                  :class="currentCourse[0].baseYear"
                >
                  {{ $t('unitStudy.sections.userNotes') }}
                </div>
                <v-divider class="dashed" style="margin-top: 15px"></v-divider>
              </div>

              <div
                v-html="u.unitNotes"
                class="_print-content-container mb-7 mt-3"
              ></div>
            </div>

            <div v-if="u.footnotesBodyContent" class="break-page-before">
              <div class="flex-container">
                <div
                  class="font-weight-bold text-h6 base-year"
                  :class="currentCourse[0].baseYear"
                >
                  {{ $t('unitStudy.sections.footnotes') }}
                </div>
                <v-divider class="dashed" style="margin-top: 15px"></v-divider>
              </div>

              <div
                v-html="u.footnotesBodyContent"
                class="_print-content-container mb-7 mt-3"
              ></div>
            </div>
          </v-flex>
        </div>
      </div>
      <div v-else>{{ $t('unitStudy.noUnit') }}</div>
    </div>
  </div>
</template>

<script>
  import db from '@/firebase/init'
  import { scopeToChurch } from '@/helpers/churchScope'
  import firebase from 'firebase'
  import axios from 'axios'
  import { mapState } from 'vuex'
  import { mapGetters } from 'vuex'
  import UserStudyQuestions from '@/components/utilities/UserStudyQuestions'
  import UserVersesQuotes from '@/components/utilities/UserVersesQuotes'
  import { assignUnitStudyQuestionNumbers } from '@/helpers/assignUnitStudyQuestionNumbers'
  import {
    applyLocalizedWeekFields,
    resolveLocalizedWeek,
  } from '@/helpers/localizeWeekContent'
  // import UserEditUnit from '@/components/utilities/UserEditUnit'

  export default {
    name: 'Pdf',
    components: {
      // UserEditUnit,
      UserStudyQuestions,
      UserVersesQuotes,
    },
    data() {
      return {
        feedback: null,
        snackbar: true,
        unit: [],
        currentCourse: [],
        unitbackdoor: 0,
        backdoor: 0,
        listenConfirmed: null,
        completeConfirmed: null,
        continueConfirmed: null,
        onLine: navigator.onLine,
        sectionTitle: '',
        commentsArray: [],
      }
    },
    watch: {
      '$route.params.sectionID': {
        handler: function (search) {
          // console.log('watch ', search)
          if (search) this.init()
        },
        deep: true,
        immediate: true,
      },
    },
    computed: {
      ...mapState('UserData', ['user']),
      ...mapState('AppData', ['userActiveCourse']),
      ...mapGetters('AppData', ['getCourse', 'getActiveCourse']),
      ...mapGetters('UserData', ['getRoles']),
      listenLabel: {
        get() {
          return this.unit.listenLabel
            ? this.unit.listenLabel.toUpperCase()
            : 'LISTEN'
        },
        set(value) {
          return value
        },
      },
      completeLabel: {
        get() {
          return this.unit.completeLabel
            ? this.unit.completeLabel.toUpperCase()
            : 'COMPLETE'
        },
        set(value) {
          return value
        },
      },
      continueLearningLabel: {
        get() {
          return this.unit.continueLearningLabel
            ? this.unit.continueLearningLabel.toUpperCase()
            : 'CONTINUE'
        },
        set(value) {
          return value
        },
      },
    },
    mounted() {
      window.addEventListener('online', this.updateOnlineStatus)
      window.addEventListener('offline', this.updateOnlineStatus)
    },
    beforeUnmount() {
      window.removeEventListener('online', this.updateOnlineStatus)
      window.removeEventListener('offline', this.updateOnlineStatus)
    },
    methods: {
      contentHasLoaded(payload) {
        // once the study questions have loaded create a div
        this.loadingComplete = payload.loadingComplete
        // console.log('1 ', this.loadingComplete)
        let loadedDiv = document.createElement('div')
        loadedDiv.setAttribute('class', 'content-has-loaded')
        document.body.appendChild(loadedDiv)
      },
      updateOnlineStatus(e) {
        const { type } = e
        this.onLine = type === 'online'
      },
      printPDF() {
        // window.print();
        // return false;
        try {
          if (!document.execCommand('print', false, null)) {
            window.print()
          }
        } catch {
          window.print()
        }
      },
      async getSectionTitle(sectionID) {
        // console.log('getSectionTitle ', sectionID)
        const docRef = db.collection('sections').doc(sectionID)
        docRef
          .get()
          .then((doc) => {
            if (doc.exists) {
              // console.log("Document data:", doc.data().title)
              this.sectionTitle = doc.data().title
            } else {
              // doc.data() will be undefined in this case
              console.log('No such document!')
            }
          })
          .catch((error) => {
            console.log('Error getting document:', error)
          })
      },
      async init() {
        //   console.log(`${this.$s}`)
        this.backdoor++

        this.unit = []
        this.currentCourse = []
        this.unitbackdoor++

        // let unitID = this.$route.params.id
        let courseID = this.$route.params.courseID
        let sectionID = this.$route.params.sectionID

        this.getSectionTitle(sectionID)

        let currentCourseRef = db.collection('currentcourses')
        currentCourseRef = currentCourseRef.where('storageID', '==', courseID)
        currentCourseRef = scopeToChurch(currentCourseRef, this.$store)
        let activeRef = await currentCourseRef.get()

        if (!activeRef.empty) {
          const doc = activeRef.docs[0]
          let course = doc.data()
          course.id = doc.id

          const storage = firebase.storage()
          const storageRef = storage.ref()
          const objectRef = storageRef.child(course.storageRef)
          let url = await objectRef.getDownloadURL()
          let resp = await axios.get(url)

          const missionRef = firebase.storage().ref().child(course.missions)
          let missionURL = await missionRef.getDownloadURL()
          let missionResp = await axios.get(missionURL)

          resp.data.missions = missionResp.data

          this.currentCourse.push(resp.data)

          const currentSection = this.currentCourse[0].sections.filter(
            (item) => item.id === sectionID
          )
          const requiredLanguage =
            currentSection[0]?.requiredLanguage ||
            this.currentCourse[0]?.requiredLanguage ||
            'en'
          // const currentSection = this.userActiveCourse[0].sections.filter(item => item.id === sectionID)
          // const currentUnit = currentSection[0].units.filter(u => u.id === unitID)

          const unitIDs = currentSection[0].units.map((u) => u.id)
          // console.log('1 ', unitIDs)
          for (var i = 0; i < unitIDs.length; i++) {
            const currentUnit = currentSection[0].units.filter(
              (u) => u.id === unitIDs[i]
            )
            // console.log('unitIDs ', currentUnit)

            /**************** */

            // get the current mission and supplication
            if (this.currentCourse[0].missions) {
              const currentWeek = this.currentCourse[0].missions.filter(
                (item) => item.unitID == currentUnit[0].id
              )

              if (currentWeek.length) {
                const localizedWeek = await resolveLocalizedWeek(
                  db,
                  currentWeek[0],
                  requiredLanguage
                )
                applyLocalizedWeekFields(
                  currentUnit[0],
                  localizedWeek,
                  currentWeek[0]
                )
              }
            }

            let currentCourseContentRef = db.collection('coursecontent')
            currentCourseContentRef = currentCourseContentRef.where(
              'courseID',
              '==',
              courseID
            )
            currentCourseContentRef = currentCourseContentRef.where(
              'sectionID',
              '==',
              sectionID
            )
            currentCourseContentRef = currentCourseContentRef.where(
              'unitID',
              '==',
              unitIDs[i]
            )
            currentCourseContentRef =
              currentCourseContentRef.orderBy('unitNotes')
            let activeCourseContentRef = await currentCourseContentRef.get()
            // console.log('activeCourseContentRef ', activeCourseContentRef)
            if (!activeCourseContentRef.empty) {
              const notesdoc = activeCourseContentRef.docs[0]
              let note = notesdoc.data()
              note.id = notesdoc.id

              // remove the <br> tags from the paragraphs in the Notes
              let cleanParagraphs = note.unitNotes.replace(/<br>\\*/g, '')

              // console.log('note ', cleanParagraphs)
              // currentUnit[0].unitNotes = note.unitNotes
              currentUnit[0].unitNotes = cleanParagraphs
            }
            this.unit.push(assignUnitStudyQuestionNumbers(currentUnit[0]))
            this.getContent(unitIDs[i])

            /******************** */
          }
          // console.log('unit ', this.unit)
        }
      },
      getContent(unitID) {
        let courseID = this.$route.params.courseID
        let sectionID = this.$route.params.sectionID
        // let unitID = this.$route.params.id

        // this.commentsArray = []

        // console.log('getContent ', unitID)

        var coursecontentRef = db.collection('coursecontent')
        coursecontentRef = coursecontentRef.where('courseID', '==', courseID)
        coursecontentRef = coursecontentRef.where('sectionID', '==', sectionID)
        coursecontentRef = coursecontentRef.where('unitID', '==', unitID)
        coursecontentRef
          .get()
          .then((snapshot) => {
            // console.log(courseID, sectionID, unitID)
            // console.log(snapshot)
            if (!snapshot.empty) {
              snapshot.forEach((doc) => {
                let uu = doc.data()
                // console.log('uu ', uu, this.unit)
                if (
                  uu.listenConfirmed ||
                  uu.completeConfirmed ||
                  uu.continueConfirmed
                ) {
                  uu.id = doc.id
                  // this.userUnit.push(uu)
                  this.listenConfirmed = uu.listenConfirmed
                  this.completeConfirmed = uu.completeConfirmed
                  this.continueConfirmed = uu.continueConfirmed
                }
                if (uu.paragraphID && uu.paragraphID !== null) {
                  // regex to grab the paragraph tags
                  // let regex = new RegExp(/<([^\s]+).*?id="([^"]*?)".*?>(.+?)<\/\1>/gi)

                  // regex to grab the paragraph and image tags
                  let regex = new RegExp(
                    /<([^\s]+).*?id="([^"]*?)".*?>(.+?)<\/\1>|<img[^>]* src="([^"]*)"[^>]*>/gi
                  )

                  let which = uu.paragraphID.split('-')
                  // console.log(this.unit[0].truthBodyContent)
                  let arrayOfParagraphs = []
                  if (which[1] == 'tbc')
                    arrayOfParagraphs =
                      this.unit[0].truthBodyContent.match(regex)
                  if (which[1] == 'ibc')
                    arrayOfParagraphs = this.unit[0].truthInBible.match(regex)
                  if (which[1] == 'ebc')
                    arrayOfParagraphs =
                      this.unit[0].equipBodyContent.match(regex)
                  if (which[1] == 'abc')
                    arrayOfParagraphs =
                      this.unit[0].accountBodyContent.match(regex)
                  if (which[1] == 'mbc')
                    arrayOfParagraphs =
                      this.unit[0].missionBodyContent.match(regex)
                  if (which[1] == 'sbc')
                    arrayOfParagraphs =
                      this.unit[0].supplicationBodyContent.match(regex)
                  if (which[1] == 'fbc')
                    arrayOfParagraphs =
                      this.unit[0].footnotesBodyContent.match(regex)

                  let searchterm = 'id="' + uu.paragraphID + '"'
                  // console.log('1 ', uu.paragraphID)

                  for (var i = 0; i < arrayOfParagraphs.length; i++) {
                    if (arrayOfParagraphs[i].search(searchterm) != -1) {
                      arrayOfParagraphs[i] =
                        '<p id="' +
                        uu.paragraphID +
                        '">' +
                        uu.paragraph +
                        '</p>'
                    }
                  }
                  if (which[1] == 'tbc')
                    this.unit[0].truthBodyContent = arrayOfParagraphs
                      .join('')
                      .toString()
                  if (which[1] == 'ibc')
                    this.unit[0].truthInBible = arrayOfParagraphs
                      .join('')
                      .toString()
                  if (which[1] == 'ebc')
                    this.unit[0].equipBodyContent = arrayOfParagraphs
                      .join('')
                      .toString()
                  if (which[1] == 'abc')
                    this.unit[0].accountBodyContent = arrayOfParagraphs
                      .join('')
                      .toString()
                  if (which[1] == 'mbc')
                    this.unit[0].missionBodyContent = arrayOfParagraphs
                      .join('')
                      .toString()
                  if (which[1] == 'sbc')
                    this.unit[0].supplicationBodyContent = arrayOfParagraphs
                      .join('')
                      .toString()
                  if (which[1] == 'fbc')
                    this.unit.footnotesBodyContent = arrayOfParagraphs
                      .join('')
                      .toString()
                }

                if (uu.commentParentID && uu.commentParentID !== null) {
                  this.commentsArray.push(uu)
                }
              })
            }
          })
          .then(() => {
            // console.log('after ', this.unit.truthInBible)
            this.commentHandler()
          })
      },
      studyQuestionsChild(value) {
        // console.log('studyQuestionsChild ', value)
        // update the property with any Study Questions
        if (value.section == 'truthStudyQuestions') {
          if (value.questions.length)
            this.unit.truthStudyQuestions = value.questions
          else this.unit.truthStudyQuestions = []
        }

        if (value.section == 'equipStudyQuestions') {
          if (value.questions.length)
            this.unit.equipStudyQuestions = value.questions
          else this.unit.equipStudyQuestions = []
        }

        if (value.section == 'accountStudyQuestions') {
          if (value.questions.length)
            this.unit.accountStudyQuestions = value.questions
          else this.unit.accountStudyQuestions = []
        }
      },
      versesQuotesChild(value) {
        // update the property with any Study Questions
        if (value.section == 'truthVersesQuotes') {
          if (value.versesquotes.length)
            this.unit.truthVersesQuotes = value.versesquotes
          else this.unit.truthVersesQuotes = []
        }
      },
      commentHandler() {
        // console.log('commentHandler')

        let hasCommentContainer =
          document.getElementsByClassName('comment-container')
        while (hasCommentContainer[0]) {
          hasCommentContainer[0].parentNode.removeChild(hasCommentContainer[0])
        }
        let hasCommentIcon = document.getElementsByClassName(
          'comment-icon-container'
        )
        while (hasCommentIcon[0]) {
          hasCommentIcon[0].parentNode.removeChild(hasCommentIcon[0])
        }

        // console.log('hasDiv ', hasDiv)
        /** add commenting module to each parapgraph **/
        let allParapgraphs = document.querySelectorAll('p')
        allParapgraphs.forEach((doc) => {
          if (doc.id && doc.textContent) {
            // add class to the paragraph
            doc.className = 'allow-commenting'

            // create a div
            var div = document.createElement('div')

            // add a class to the div
            div.className = 'comment-container'
            // console.log('1 ', this.commentsArray)
            const theComment = this.commentsArray.filter((el) => {
              if (el.commentParentID === doc.id) return el.comment

              return
            })

            // add the match to the div
            if (theComment.length > 0) {
              // add the comment inside the div
              let textNode = document.createTextNode(theComment[0].comment)
              div.append(textNode)

              // show the comment
              doc.classList.add('active')
            }

            // insert div at the beginning of the paragraph
            doc.insertBefore(div, doc.firstChild)
          }
        })
        /** . commenting module **/
      },
    },
  }
</script>

<style scoped>
  [v-cloak] {
    display: none;
  }
  /* .unit h2 {
    font-size: .95em;
} */

  @media print {
    #truth
      :deep(p, )
      #equip
      :deep(p, )
      #application
      :deep(p, )
      #mission
      :deep(p, )
      #supplication
      :deep(p, )
      #truth
      :deep(img, )
      #equip
      :deep(img, )
      #application
      :deep(img, )
      #mission
      :deep(img, )
      #supplication
      :deep(img) {
      page-break-before: auto;
      page-break-after: auto;
      break-before: auto;
      break-after: auto;
      page-break-inside: avoid !important;
      break-inside: avoid !important;
      padding-top: 16px;
      padding-bottom: 12px;
      margin-bottom: 0px;
    }
    .break-page-before {
      page-break-before: always;
      padding-top: 5mm;
    }
    .break-page-after {
      page-break-after: always;
      padding-top: 5mm;
      /* margin-top: 25mm;
        margin-bottom: 25mm; */
    }
    .print-content-container,
    .study-question-statement {
      page-break-inside: avoid;
      break-inside: avoid;
      display: block;
    }
    .unit.container .pdf-background-image {
      background: #fff !important;
      width: 100% !important;
      height: 100% !important;
    }
    .hideonprint {
      display: none;
    }
  }

  #truth
    :deep(p, )
    #equip
    :deep(p, )
    #application
    :deep(p, )
    #mission
    :deep(p, )
    #supplication
    :deep(p, )
    #truth
    :deep(img, )
    #equip
    :deep(img, )
    #application
    :deep(img, )
    #mission
    :deep(img, )
    #supplication
    :deep(img) {
    page-break-before: auto;
    page-break-after: auto;
    break-before: auto;
    break-after: auto;
    page-break-inside: avoid !important;
    break-inside: avoid !important;
    padding-top: 16px;
    padding-bottom: 12px;
    margin-bottom: 0px;
    display: inline-block;
  }

  .print-content-container,
  .study-question-statement {
    page-break-inside: avoid;
    break-inside: avoid;
    display: block;
  }

  .unit.container {
    max-width: 600px;
  }
  .unit.container .background-img-container {
    position: fixed;
    z-index: -1;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    overflow: hidden;
  }
  .unit.container .pdf-background-image {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    /* background: url(../../assets/mountains_bw.jpg) no-repeat 50% 0; */
    background: #fff;
    background-size: cover;
    -webkit-transform: translate3d(0, 0, 0);
    transform: translate3d(0, 0, 0);
    /*filter: grayscale(100%) blur(0px);*/
  }
  .theme--dark .print-pdf {
    color: #000;
  }
  .print-pdf .theme--dark.v-divider.dashed {
    background-image: linear-gradient(
      to right,
      #acacac 33%,
      rgba(255, 255, 255, 0) 0%
    );
  }
  .theme--dark .unit.container .background-image {
    filter: grayscale(100%) blur(0px) invert(1);
  }
  .pdf-background-image-donotuse:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.85);
  }
  .unit .contentSection {
    /* margin-bottom: 50%; */
    /* max-height: 90vh;
    overflow: hidden; */
    position: relative;
  }
  .edit-user-unit-container.blueYear :deep(a) {
    color: #002b45;
  }
  .edit-user-unit-container.redYear :deep(a) {
    color: #6e273d;
  }
  .edit-user-unit-container.greenYear :deep(a) {
    color: #024731;
  }
  .flex-this {
    display: flex;
  }
  .edit-user-unit-container :deep(img) {
    max-width: 100%;
  }
  .unit-assignments :deep(p) {
    margin-bottom: 0;
  }
  .unit-assignments :deep(label) {
    text-transform: none;
  }
  .label-text {
    margin-left: 5px;
    font-size: 13px;
    padding-top: 2px;
  }
  .unit-assignments :deep(.v-input__slot) {
    align-items: start;
  }
  .unit-icon.blueYear,
  .theme--dark .unit-icon.blueYear {
    border: 0px solid #002b45;
    background: #002b45;
    color: #ffffff;
  }
  .unit-icon.greenYear,
  .theme--dark .unit-icon.greenYear {
    border: 0px solid #024731;
    background: #024731;
    color: #fff;
  }
  .unit-icon.redYear,
  .theme--dark .unit-icon.redYear {
    border: 0px solid #6e273d;
    background: #6e273d;
    color: #ffffff;
  }
  .edit-user-unit-container :deep(.comment-container) {
    opacity: 0;
    height: 0;
  }
  .edit-user-unit-container :deep(.active .comment-container) {
    opacity: 1;
    width: auto;
    position: relative;
    top: -5px;
    border-radius: 4px;
    padding: 10px;
    color: #fff;
    height: auto;
  }
  .edit-user-unit-container.blueYear :deep(.comment-container) {
    background: #002b45;
  }
  .edit-user-unit-container.redYear :deep(.comment-container) {
    background: #6e273d;
  }
  .edit-user-unit-container.greenYear :deep(.comment-container) {
    background: #024731;
  }
  .edit-user-unit-container :deep(.active .comment-container:after) {
    content: '';
    position: absolute;
    left: 50%;
    bottom: -5px;
    transform: translateX(-50%);
    width: 0px;
    height: 0px;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    /* border-top: 6px solid rgb(56, 57, 53); */
  }
  .edit-user-unit-container.blueYear :deep(.comment-container:after) {
    border-top: 6px solid #002b45;
  }
  .edit-user-unit-container.redYear :deep(.comment-container:after) {
    border-top: 6px solid #6e273d;
  }
  .edit-user-unit-container.greenYear :deep(.comment-container:after) {
    border-top: 6px solid #024731;
  }
  @media only screen and (max-width: 767px) {
    .flex-this {
      flex-direction: column;
    }
  }

  .print-content-container :deep(img) {
    page-break-inside: avoid;
    break-inside: avoid;
  }

  /* --- keep comment bubbles + highlights when printing to PDF --- */
  @media print {
    /* Tell the print engine to honor background colors */
    .print-pdf,
    .print-pdf * {
      -webkit-print-color-adjust: exact !important; /* Chrome/Safari */
      print-color-adjust: exact !important; /* Spec */
      color-adjust: exact !important; /* Legacy */
    }

    /* Ensure comment chips are visible/colored in print */
    .print-pdf .comment-container {
      opacity: 1 !important;
      height: auto !important;
    }

    /* Keep the little triangle pointer colored too */
    .print-pdf .comment-container:after {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }

    /* Highlights (adjust selectors to whatever you use) */
    .print-pdf mark,
    .print-pdf .highlight,
    .print-pdf .hl,
    .print-pdf [data-highlight='true'] {
      background-color: #fde68a !important; /* your highlight color */
    }
  }
</style>
